/**
 * Schedule local notifications for dashboard reminders so they fire when app is closed.
 * When app is in foreground, shows an in-app alert.
 * Requires @notifee/react-native. If not installed, scheduling is a no-op.
 */
import { Platform, Alert } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { t } from '../../../shared/i18n/core';
import type { DashboardReminder } from './types';

const REMINDER_CHANNEL_ID = 'dashboard_reminders';
const REMINDER_TAG_PREFIX = 'rem_';
/** Must match `REMINDERS_KEY` in `useDashboardPreferences.ts` (foreground listener reads persisted enabled flags). */
const REMINDERS_STORAGE_KEY = 'dashboard_reminders';

let notifee: any = null;
let TriggerType: { TIMESTAMP: number } | null = null;
let EventType: { DELIVERED: number; PRESS: number } | null = null;
try {
  const notifee_module = require('@notifee/react-native');
  notifee = notifee_module.default;
  TriggerType = notifee_module.TriggerType ?? null;
  EventType = notifee_module.EventType ?? null;
} catch {
  // Notifee not installed; scheduling will no-op
}

function logReminderError(message: string, err?: unknown): void {
  if (__DEV__ && (typeof console !== 'undefined' && console.warn)) {
    console.warn('[ReminderNotifications]', message, err ?? '');
  }
}

function logReminder(message: string, _data?: object): void {
  void message;
}

export async function requestReminderPermissions(): Promise<boolean> {
  if (!notifee) {return false;}
  try {
    const permission_options =
      Platform.OS === 'ios'
        ? { alert: true, sound: true, badge: true }
        : undefined;
    const settings = await notifee.requestPermission(permission_options);
    const status = settings?.authorizationStatus ?? -1;
    // Notifee: -1 NOT_DETERMINED, 0 DENIED, 1 AUTHORIZED, 2 PROVISIONAL. Grant if AUTHORIZED or PROVISIONAL.
    return status >= 1;
  } catch (err) {
    logReminderError('requestReminderPermissions failed', err);
    return false;
  }
}

/**
 * On Android 12+, timestamp triggers need SCHEDULE_EXACT_ALARM for exact delivery.
 * If disabled, opens system settings so user can enable; we still schedule (may be inexact).
 */
async function ensureAndroidAlarmPermission(): Promise<void> {
  if (!notifee || Platform.OS !== 'android') {return;}
  try {
    const settings = await notifee.getNotificationSettings();
    const android = settings?.android;
    if (!android || android.alarm === 1) {return;}
    if (typeof notifee.openAlarmPermissionSettings === 'function') {
      await notifee.openAlarmPermissionSettings();
    }
  } catch (_) {}
}

export async function createReminderChannel(): Promise<void> {
  if (!notifee) {return;}
  try {
    await notifee.createChannel({
      id: REMINDER_CHANNEL_ID,
      name: 'Reminders',
      importance: 4,
      sound: 'default',
    });
  } catch (err) {
    logReminderError('createReminderChannel failed', err);
  }
}

async function cancelAllReminderNotifications(): Promise<void> {
  if (!notifee) {return;}
  try {
    const ids = await notifee.getTriggerNotificationIds();
    for (const id of ids) {
      if (id.startsWith(REMINDER_TAG_PREFIX)) {
        await notifee.cancelTriggerNotification(id);
      }
    }
  } catch (err) {
    logReminderError('cancelAllReminderNotifications failed', err);
  }
}

/**
 * Schedule local notifications for all enabled reminders.
 * Call on app launch and whenever reminders list changes.
 * Requests permission first; if denied, scheduling is skipped.
 * When Notifee is not installed, this no-ops.
 */
export async function scheduleReminderNotifications(reminders: DashboardReminder[]): Promise<void> {
  logReminder('scheduleReminderNotifications called', { total: reminders.length, enabled: reminders.filter((r) => r.enabled).length });
  if (!notifee) {
    logReminder('Notifee not available; skipping schedule');
    return;
  }
  const enabled = reminders.filter((r) => r.enabled);

  try {
    await cancelAllReminderNotifications();
  } catch (err) {
    logReminderError('cancelAllReminderNotifications failed', err);
  }

  if (enabled.length === 0) {
    logReminder('No enabled reminders; triggers cleared');
    return;
  }

  const granted = await requestReminderPermissions();
  logReminder('Permission result', { granted });
  if (!granted) {
    logReminderError('Notification permission not granted; reminders will not fire when app is closed.');
    return;
  }

  await ensureAndroidAlarmPermission();
  await createReminderChannel();

  const now = Date.now();
  const SCHEDULE_AHEAD_COUNT = 5;
  const trigger_type = TriggerType?.TIMESTAMP ?? 1;
  const min_future_ms = 1000; // require at least 1 second in future (some platforms ignore past timestamps)

  for (const rem of enabled) {
    const interval_ms = rem.interval_minutes * 60 * 1000;
    const start = rem.last_trigger_at ?? rem.created_at ?? now;

    // First occurrence must be in the future; advance until we get there
    let next_trigger = start + interval_ms;
    while (next_trigger <= now + min_future_ms) {
      next_trigger += interval_ms;
    }

    logReminder('Scheduling reminder', { id: rem.id, label: rem.label, interval_minutes: rem.interval_minutes, first_trigger_in_sec: Math.round((next_trigger - now) / 1000) });

    for (let i = 0; i < SCHEDULE_AHEAD_COUNT; i++) {
      const trigger_at = next_trigger + i * interval_ms;
      if (trigger_at <= now) {continue;}
      const notif_id = `${REMINDER_TAG_PREFIX}${rem.id}_${i}`;
      try {
        await notifee.createTriggerNotification(
          {
            id: notif_id,
            title: rem.label,
            body: 'Reminder',
            android: {
              channelId: REMINDER_CHANNEL_ID,
              smallIcon: 'ic_launcher',
              pressAction: { id: 'default' },
            },
            ios: {
              sound: 'default',
            },
            data: { reminderId: String(rem.id) },
          },
          {
            type: trigger_type,
            timestamp: trigger_at,
            ...(Platform.OS === 'android' && { alarmManager: true }),
          }
        );
        logReminder('Scheduled trigger', { notif_id, trigger_at: new Date(trigger_at).toISOString() });
      } catch (err) {
        logReminderError(`createTriggerNotification failed for ${notif_id}`, err);
      }
    }
  }
}

const REMINDER_DATA_KEY = 'reminderId';

/**
 * Subscribe to reminder notifications when app is in foreground.
 * Shows an in-app Alert so the user sees the reminder from anywhere in the app.
 * Call once at app startup and use the returned function to unsubscribe on cleanup.
 */
export function setupReminderForegroundListener(): (() => void) | void {
  if (!notifee || typeof notifee.onForegroundEvent !== 'function') {return;}
  const event_type_delivered = EventType?.DELIVERED ?? 3;
  const event_type_press = EventType?.PRESS ?? 1;

  logReminder('setupReminderForegroundListener: subscribed (reminders will log "REMINDER FIRED" when they fire)');

  const unsubscribe = notifee.onForegroundEvent(
    async (event: { type: number; detail: { notification?: { id?: string; title?: string; body?: string; data?: Record<string, string> } } }) => {
      const { type, detail } = event;
      logReminder('Foreground event', { type, notificationId: detail?.notification?.id, data: detail?.notification?.data });
      const notif = detail?.notification;
      const raw_reminder_id = notif?.data?.[REMINDER_DATA_KEY];
      const is_reminder = raw_reminder_id != null && String(raw_reminder_id).length > 0;
      if (!is_reminder || !notif) {return;}

      try {
        const stored = await AsyncStorage.getItem(REMINDERS_STORAGE_KEY);
        const list: DashboardReminder[] = stored ? JSON.parse(stored) : [];
        const rem = list.find(r => r.id === String(raw_reminder_id));
        if (!rem || rem.enabled === false) {
          logReminder('Skipping reminder alert: disabled or removed', { id: raw_reminder_id });
          return;
        }
      } catch (err) {
        logReminderError('reminder foreground: read storage failed', err);
        return;
      }

      const fallback_title = t('reminders.reminderDefaultLabel');
      const title = notif.title ?? fallback_title;
      const body = notif.body ?? '';

      if (type === event_type_delivered || type === event_type_press) {
        logReminder('REMINDER FIRED', { event: type === event_type_delivered ? 'DELIVERED' : 'PRESS', title, body, reminderId: notif.data?.[REMINDER_DATA_KEY] });
        Alert.alert(title, body || fallback_title, [{ text: t('common.ok') }]);
      }
    },
  );

  return unsubscribe;
}

/** Call to check if Notifee is available (e.g. to show a message if reminders cannot work). */
export function isReminderNotificationsAvailable(): boolean {
  return notifee != null;
}
